import { useEffect, useState } from "react";
import { useGetIdentity, useStore } from "ra-core";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { NOTIFICATIONS_ENABLED_STORE_KEY } from "../settings/userPreferences";

const DISMISS_KEY = "crm.notificationsBannerDismissed";

/**
 * Soft prompt when the account wants notifications but this device is not
 * subscribed yet (browsers block silent permission prompts).
 */
export function DeviceNotificationsBanner() {
  const { data: identity } = useGetIdentity();
  const salesId = (identity?.id as number | null) ?? null;
  const [prefEnabled] = useStore<boolean>(NOTIFICATIONS_ENABLED_STORE_KEY, true);
  const identityPref =
    (identity as { notifications_enabled?: boolean | null } | undefined)
      ?.notifications_enabled ?? true;
  const wantsNotifications = prefEnabled !== false && identityPref !== false;

  const {
    permissionState,
    isSubscribed,
    isLoading,
    subscribe,
  } = usePushNotifications(salesId);

  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!wantsNotifications) setDismissed(false);
  }, [wantsNotifications]);

  if (
    dismissed ||
    !salesId ||
    isLoading ||
    !wantsNotifications ||
    isSubscribed ||
    permissionState === "unsupported" ||
    permissionState === "denied"
  ) {
    return null;
  }

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // ignore
    }
    setDismissed(true);
  };

  return (
    <div className="sticky top-0 z-40 border-b bg-muted/80 px-4 py-2 backdrop-blur">
      <div className="mx-auto flex max-w-[90rem] flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Notifications are enabled on your account. Enable them on this device
          to get task reminders.
        </p>
        <div className="flex shrink-0 gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={dismiss}>
            Not now
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => {
              void subscribe().then(dismiss);
            }}
          >
            <Bell className="mr-1.5 h-4 w-4" />
            Enable on this device
          </Button>
        </div>
      </div>
    </div>
  );
}
